import { useCallback, useEffect, useRef, useState } from "react";

import { notificationsService } from "./notifications.service";
import { SparePartsNotification } from "./types";

const KNOWN_NOTIFICATION_IDS_KEY = "spare-parts-known-notification-ids";
const MAX_KNOWN_NOTIFICATION_IDS = 100;

const loadKnownNotificationIds = () => {
  try {
    const value = sessionStorage.getItem(KNOWN_NOTIFICATION_IDS_KEY);
    const ids = value ? JSON.parse(value) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : []);
  } catch {
    return new Set<string>();
  }
};

const saveKnownNotificationIds = (ids: Set<string>) => {
  try {
    sessionStorage.setItem(
      KNOWN_NOTIFICATION_IDS_KEY,
      JSON.stringify(Array.from(ids).slice(-MAX_KNOWN_NOTIFICATION_IDS))
    );
  } catch {
    // Storage is optional and must not prevent notifications from loading.
  }
};

export const useSparePartsNotifications = () => {
  const [notifications, setNotifications] = useState<SparePartsNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [latestNotification, setLatestNotification] =
    useState<SparePartsNotification | null>(null);
  const knownNotificationIds = useRef(loadKnownNotificationIds());
  const hasLoadedInitialNotifications = useRef(knownNotificationIds.current.size > 0);
  const audioContext = useRef<AudioContext | null>(null);
  const audioUnlocked = useRef(false);

  const enableNotificationSound = useCallback(() => {
    try {
      if (!audioContext.current || audioContext.current.state === "closed") {
        audioContext.current = new window.AudioContext();
      }

      const context = audioContext.current;
      if (context.state === "running") {
        audioUnlocked.current = true;
        return;
      }

      // This is called from the user's interaction event. Browsers only allow
      // a sound context to be resumed from that event, not later from polling.
      void context
        .resume()
        .then(() => {
          audioUnlocked.current = context.state === "running";
        })
        .catch(() => {
          audioUnlocked.current = false;
        });
    } catch {
      // Audio remains optional when a browser does not support Web Audio.
      audioUnlocked.current = false;
    }
  }, []);

  useEffect(() => {
    window.addEventListener("pointerdown", enableNotificationSound, true);
    window.addEventListener("keydown", enableNotificationSound, true);

    return () => {
      window.removeEventListener("pointerdown", enableNotificationSound, true);
      window.removeEventListener("keydown", enableNotificationSound, true);
      const context = audioContext.current;
      audioContext.current = null;
      audioUnlocked.current = false;
      void context?.close();
    };
  }, [enableNotificationSound]);

  const playNotificationSound = useCallback(() => {
    try {
      const context = audioContext.current;
      if (!context || !audioUnlocked.current || context.state !== "running") return;

      const playBeep = () => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const now = context.currentTime;

        oscillator.type = "square";
        oscillator.frequency.setValueAtTime(1_080, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.55, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        gain.gain.setValueAtTime(0.0001, now + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.55, now + 0.21);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
        gain.gain.setValueAtTime(0.0001, now + 0.4);
        gain.gain.exponentialRampToValueAtTime(0.55, now + 0.41);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.56);

        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.6);
      };

      playBeep();
    } catch {
      // Browsers can block sound until the user has interacted with the page.
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const [notificationResult, count] = await Promise.all([
        notificationsService.getNotifications({ page: 1, limit: 20 }),
        notificationsService.getUnreadCount(),
      ]);

      const latest = notificationResult.data || [];
      const incoming = latest.filter(
        (notification) => !knownNotificationIds.current.has(notification.id)
      );

      latest.forEach((notification) => {
        knownNotificationIds.current.add(notification.id);
      });
      saveKnownNotificationIds(knownNotificationIds.current);

      if (hasLoadedInitialNotifications.current && incoming.length > 0) {
        const newest = [...incoming].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        setLatestNotification(newest);
        playNotificationSound();
      }

      hasLoadedInitialNotifications.current = true;
      setNotifications(latest);
      setUnreadCount(count || 0);
    } catch (error) {
      console.error("Unable to refresh Spare Parts notifications", error);
    } finally {
      setLoading(false);
    }
  }, [playNotificationSound]);

  const markRead = useCallback(
    async (id: string) => {
      await notificationsService.markRead(id);
      await refresh();
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    await notificationsService.markAllRead();
    await refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();

    const interval = window.setInterval(refresh, 10_000);

    return () => window.clearInterval(interval);
  }, [refresh]);

  return {
    notifications,
    unreadCount,
    loading,
    latestNotification,
    clearLatestNotification: () => setLatestNotification(null),
    enableNotificationSound,
    refresh,
    markRead,
    markAllRead,
  };
};
